import { z } from "zod";

import { LinkedinSenderState } from "@signalist/shared-types";

import { FetchMeResponseSchema } from "@/pages/Public/pages/Login/schema/login";
import { MetaDataSchema } from "@/schemas/meta-data";

export const GetAllUsersResponseSchema = z.object({
	data: z.array(FetchMeResponseSchema),
	meta: MetaDataSchema,
});

export const GetAllSendersUsersResponseSchema = z.object({
	data: z.array(FetchMeResponseSchema),
});

export const CompanyUserSchema = z.object({
	id: z.number(),
	firstName: z.string().nullable().optional(),
	lastName: z.string().nullable().optional(),
});

export const GetCompanyUsersResponseSchema = z.object({
	data: z.array(CompanyUserSchema),
});

export type CompanyUser = z.infer<typeof CompanyUserSchema>;
export type GetCompanyUsersResponse = z.infer<typeof GetCompanyUsersResponseSchema>;

/** A user of the company as a possible sender, with the state of their LinkedIn account (PREC-4244). */
export const SenderCandidateSchema = z.object({
	id: z.number(),
	firstName: z.string().nullable().optional(),
	lastName: z.string().nullable().optional(),
	linkedin: z.enum(["connected", "disconnected", "none"]),
});

export const GetSenderCandidatesResponseSchema = z.object({
	data: z.array(SenderCandidateSchema),
});

export type SenderCandidate = z.infer<typeof SenderCandidateSchema>;
export type GetSenderCandidatesResponse = z.infer<typeof GetSenderCandidatesResponseSchema>;

export const CheckConnectionResultSchema = z.object({
	userId: z.number(),
	connected: z.boolean(),
	/**
	 * What a LinkedIn message from this sender to the lead would do (PREC-4216). Answered per sender
	 * when the query names a `listPersonId`; without one only CONNECTED or NONE.
	 */
	state: z.nativeEnum(LinkedinSenderState).optional(),
	/** INVITATION_PENDING only: when the invitation stops waiting (a parked message is dropped then). */
	invitationDeadline: z.string().nullable().optional(),
});

export const CheckConnectionResponseSchema = z.object({
	results: z.array(CheckConnectionResultSchema),
});

export type CheckConnectionResult = z.infer<typeof CheckConnectionResultSchema>;
export type CheckConnectionResponse = z.infer<typeof CheckConnectionResponseSchema>;

export interface CheckConnectionQuery {
	userIds: number[];
	linkedinUrl?: string;
	urn?: string;
	/** The lead the composer writes to: the API then answers each sender's `state` (PREC-4216). */
	listPersonId?: number;
}
